import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'
import { sendReportMessage } from '../lib/pushNotifications'

interface Props {
  kind: 'sighting' | 'lost'
  reportId: number
  ownerUserId: string | null | undefined
}

export function ReportMessageForm({ kind, reportId, ownerUserId }: Props) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  if (!ownerUserId) return null
  if (!user) {
    return (
      <p className="field-help report-message-hint">{t('notify.signInToMessage')}</p>
    )
  }
  if (user.id === ownerUserId) return null

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy || !body.trim()) return
    setBusy(true)
    setError(null)
    try {
      await sendReportMessage({ kind, id: reportId, body: body.trim() })
      setSent(true)
      setBody('')
      setOpen(false)
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : t('notify.messageFailed'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="report-message">
      {sent && (
        <p className="report-message-sent" role="status">
          {t('notify.messageSent')}
        </p>
      )}
      {!open ? (
        <button
          type="button"
          className="btn"
          onClick={() => {
            setOpen(true)
            setSent(false)
          }}
        >
          {t('notify.sendMessage')}
        </button>
      ) : (
        <form className="report-message-form" onSubmit={(e) => void handleSubmit(e)}>
          <label htmlFor={`message-${kind}-${reportId}`}>
            {t('notify.messageLabel')}
          </label>
          <textarea
            id={`message-${kind}-${reportId}`}
            rows={3}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            maxLength={1000}
            required
          />
          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t('status.submitting') : t('notify.send')}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
